import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Package, ShieldCheck, Timer, ArrowDown } from "lucide-react";
import ClaimForm from "@/components/ClaimForm";
import { COUNTRIES, detectCountry } from "@/lib/countries";

const HERO_IMG =
  "https://images.unsplash.com/photo-1721937718756-3bfec49f42a2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const HEAD_LINES = ["¿Tu paquete", "no aparece en", "el sistema?"];

const CHAPTERS = [
  {
    n: "01",
    title: "Registra tu tracking",
    body: "Completa el formulario con tu código de casillero y el número del paquete que no fue cargado.",
  },
  {
    n: "02",
    title: "Un solo reclamo por paquete",
    body: "Cada número de tracking puede reclamarse una única vez. Evitamos duplicados para acelerar la respuesta.",
  },
  {
    n: "03",
    title: "Flujo automatizado",
    body: "Tus datos viajan de inmediato a nuestro sistema y activan un flujo de trabajo interno para localizar tu envío.",
  },
  {
    n: "04",
    title: "Te contactamos",
    body: "Nuestro equipo hace seguimiento y te escribe por correo electrónico con el estado real de tu paquete.",
  },
];

const STATS = [
  { value: "6h", label: "Tiempo promedio de primera respuesta" },
  { value: "1×", label: "Reclamo único por número de tracking" },
  { value: "100%", label: "Reclamos rastreados hasta su cierre" },
];

const ease = [0.16, 1, 0.3, 1];

function Header({ config }) {
  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-inkdeep/60 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
        <a href="/" className="flex items-center gap-2.5" data-testid="brand-logo">
          <img
            src={config.logoWhite}
            alt={config.marca}
            className="h-10 w-auto"
          />
        </a>
        <a
          href="#reclamo"
          data-testid="header-cta"
          className="hidden sm:inline-flex h-10 items-center rounded-sm bg-gold px-5 font-sans text-sm font-semibold text-ink transition-transform duration-200 hover:-translate-y-0.5"
        >
          Iniciar reclamo
        </a>
      </div>
    </header>
  );
}

function Hero({ config }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section
      ref={ref}
      className="grain relative overflow-hidden bg-inkdeep pt-28 pb-40 lg:pb-52"
      data-testid="hero-section"
    >
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="absolute inset-0 z-0"
      >
        <img
          src={HERO_IMG}
          alt="Almacén de logística"
          className="h-full w-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-inkdeep via-inkdeep/80 to-inkdeep/30" />
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid grid-cols-12 gap-8 items-end">
          <div className="col-span-12 lg:col-span-8">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, ease }}
              className="mb-8 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5"
            >
              <span className="h-2 w-2 rounded-full bg-gold animate-pulse" />
              <span className="font-mono text-xs uppercase tracking-[0.18em] text-white/70">
                {config.flag} Reclamos · {config.marca}
              </span>
            </motion.div>

            <h1 className="font-display font-extrabold text-white tracking-tighter text-5xl sm:text-7xl lg:text-8xl leading-[0.92]">
              {HEAD_LINES.map((line, i) => (
                <span key={i} className="line-mask">
                  <motion.span
                    className="block"
                    initial={{ y: "110%" }}
                    animate={{ y: "0%" }}
                    transition={{ duration: 0.9, ease, delay: 0.15 + i * 0.12 }}
                  >
                    {i === 2 ? (
                      <span className="text-gold">{line}</span>
                    ) : (
                      line
                    )}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease, delay: 0.7 }}
              className="mt-8 max-w-xl text-base sm:text-lg text-white/70 leading-relaxed"
            >
              Registra un reclamo por tu número de tracking y activaremos de inmediato la
              búsqueda de tu envío. Rápido, seguro y con seguimiento real.
            </motion.p>

            <motion.a
              href="#reclamo"
              data-testid="hero-cta"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease, delay: 0.85 }}
              className="group mt-10 inline-flex items-center gap-3 text-white"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-full border border-gold text-gold transition-colors duration-200 group-hover:bg-gold group-hover:text-ink">
                <ArrowDown className="h-5 w-5" />
              </span>
              <span className="font-sans text-sm font-semibold uppercase tracking-widest">
                Llenar formulario
              </span>
            </motion.a>
          </div>
        </div>
      </div>
    </section>
  );
}

function FormAndManifesto({ config }) {
  return (
    <section className="relative z-20 pt-16 lg:pt-24 pb-24">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="col-span-12 lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, ease }}
            >
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-alert">
                Cómo funciona
              </span>
              <h2 className="mt-4 font-display text-4xl sm:text-5xl font-extrabold text-ink tracking-tight leading-none">
                Un proceso claro,<br />un reclamo por paquete.
              </h2>
            </motion.div>

            <div className="mt-10 flex flex-col">
              {CHAPTERS.map((c, i) => (
                <motion.div
                  key={c.n}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, ease, delay: i * 0.05 }}
                  className="flex gap-5 border-t border-ink/10 py-6"
                  data-testid={`chapter-${c.n}`}
                >
                  <span className="font-mono text-sm text-gold pt-1">{c.n}</span>
                  <div>
                    <h3 className="font-display text-xl font-extrabold text-ink">
                      {c.title}
                    </h3>
                    <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed max-w-sm">
                      {c.body}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease }}
            className="col-span-12 lg:col-span-7 lg:-mt-52"
          >
            <ClaimForm config={config} />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Marquee({ config }) {
  const items = [...config.marquee, ...config.marquee];
  return (
    <section className="bg-gold overflow-hidden py-6 border-y-2 border-ink" data-testid="marquee">
      <div className="marquee-track">
        {items.map((t, i) => (
          <span key={i} className="flex items-center">
            <span className="font-display text-2xl sm:text-3xl font-extrabold text-ink px-8 tracking-tight">
              {t}
            </span>
            <span className="text-ink/40 font-display text-2xl">✦</span>
          </span>
        ))}
      </div>
    </section>
  );
}

function Stats() {
  const icons = [ShieldCheck, Package, Timer];
  return (
    <section className="bg-inkdeep py-24" data-testid="stats-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-12">
        {STATS.map((s, i) => {
          const Icon = icons[i];
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, ease, delay: i * 0.1 }}
              className="border-t border-white/15 pt-6"
              data-testid={`stat-${i}`}
            >
              <Icon className="h-6 w-6 text-gold mb-6" strokeWidth={1.8} />
              <div className="font-display text-6xl lg:text-7xl font-extrabold text-white tracking-tighter">
                {s.value}
              </div>
              <p className="mt-3 text-sm text-white/60 max-w-[16rem] leading-relaxed">
                {s.label}
              </p>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}

function Footer({ config }) {
  return (
    <footer className="bg-inkdeep border-t border-white/10 py-12" data-testid="footer">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
        <div className="flex items-center gap-2.5">
          <img
            src={config.logoWhite}
            alt={config.marca}
            className="h-9 w-auto"
          />
        </div>
        <p className="font-mono text-xs text-white/40">
          © {new Date().getFullYear()} {config.marca} · Formulario de reclamos
        </p>
      </div>
    </footer>
  );
}

export default function Landing() {
  const config = COUNTRIES[detectCountry()];
  return (
    <main className="bg-paper">
      <Header config={config} />
      <Hero config={config} />
      <FormAndManifesto config={config} />
      <Marquee config={config} />
      <Stats />
      <Footer config={config} />
    </main>
  );
}
